"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR, { mutate as globalMutate, type SWRConfiguration } from "swr";
import { createAuthRefresh, createClient, createTracingFetch, refreshAuth } from "@workspace/network";

/**
 * User profile data structure
 */
export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  initials?: string;
  avatarBgColor?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Return type for useAuth hook
 */
export interface UseAuthReturn {
  /** User profile data, undefined if not loaded or not authenticated */
  user: User | undefined;
  /** True while initial fetch is in progress */
  loading: boolean;
  /** True while revalidating in the background */
  validating: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** True if user is authenticated (has valid user data) */
  authenticated: boolean;
  /** Manually refresh the user data */
  refresh: () => Promise<User | undefined>;
  /** Log out the current user */
  logout: () => Promise<void>;
}

/**
 * Read the user_info cookie set by the auth BE.
 * This cookie is non-HttpOnly and travels across all *.gaz.codes subdomains,
 * giving us instant access to name/email/avatar without a network round-trip.
 */
type CookieUser = Partial<User> & {
  refreshExpiresAt?: number;
  accessExpiresAt?: number;
};

function readUserInfoCookie(): CookieUser | null {
  if (typeof document === "undefined") return null;
  // There can be more than one: a `Domain=.gaz.codes` cookie from signing in on
  // dev/prod is also sent to every *.l.gaz.codes app, beside the local one.
  // Browsers list the OLDER of two same-path cookies first, so taking the
  // first match can read a long-expired accessExpiresAt forever: the scheduler
  // refreshes at zero delay, the refresh rotates only the local cookie, and
  // the loop never ends. Take the freshest instead.
  let freshest: CookieUser | null = null;
  for (const match of document.cookie.matchAll(/(?:^|;\s*)user_info=([^;]*)/g)) {
    const candidate = parseUserInfo(match[1]);
    if (candidate && expiryOf(candidate) >= (freshest ? expiryOf(freshest) : -1)) {
      freshest = candidate;
    }
  }
  return freshest;
}

function expiryOf(cookie: CookieUser): number {
  return cookie.accessExpiresAt ?? cookie.refreshExpiresAt ?? 0;
}

function parseUserInfo(raw: string | undefined): CookieUser | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return {
      name: parsed.name ?? "",
      email: parsed.email ?? "",
      avatar: parsed.avatar ?? undefined,
      initials: parsed.initials ?? undefined,
      avatarBgColor: parsed.bgColor ?? undefined,
      refreshExpiresAt: typeof parsed.refreshExpiresAt === "number" ? parsed.refreshExpiresAt : undefined,
      accessExpiresAt: typeof parsed.accessExpiresAt === "number" ? parsed.accessExpiresAt : undefined,
    };
  } catch {
    return null;
  }
}

const tracedFetch = createTracingFetch({
  onRequest: (url, method) => console.log(`[ui-auth] → ${method} ${url}`),
  onResponse: (url, method, status, ms) =>
    console.log(`[ui-auth] ← ${status} ${method} ${url} (${ms}ms)`),
  onError: (url, method, err, ms) =>
    console.error(`[ui-auth] ✗ ${method} ${url} (${ms}ms): ${err}`),
  infraViz: { source: "ui-auth" },
});

/**
 * Auth API client using @workspace/network
 * - Includes credentials for HttpOnly cookie auth
 * - Minimal retry (auth failures shouldn't retry much)
 * - Auto-renews tokens on 401 via refresh_token cookie
 */
const authApiUrl =
  process.env.NEXT_PUBLIC_AUTH_API_URL || "https://api.auth.gaz.codes";
const authUrl = process.env.NEXT_PUBLIC_AUTH_URL || "https://auth.gaz.codes";

const authClient = createClient({
  baseUrl: authApiUrl,
  timeout: 10_000,
  retry: { attempts: 1, delay: 1000 },
  credentials: "include",
  fetch: tracedFetch,
  auth: { onUnauthorized: createAuthRefresh({ authApiUrl }) },
});

/**
 * Fetcher function for SWR using network client
 */
/**
 * Shape actually returned by `GET /v1/profile` (the `UserProfile` schema).
 *
 * It is NOT the `User` shape this hook exposes: the API says `username` and
 * `avatarUrl` where `User` says `name` and `avatar`, and it has no `initials`
 * or `avatarBgColor` at all (those live only in the `user_info` cookie).
 */
interface ProfileResponse {
  id: string;
  email: string;
  username?: string;
  avatarUrl?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Normalise the profile response into `User`.
 *
 * The cast used to be `authClient.get<User>(path)`, which type-checked while
 * silently yielding `name: undefined` — the response simply has no such key.
 * That made the header avatar flip from the username's initial to email-derived
 * initials the moment SWR resolved (e.g. "G" → "GJ"), because `getInitials`
 * falls through to the email when `username` is empty.
 *
 * `initials`/`avatarBgColor` are deliberately absent here: the profile endpoint
 * does not return them, and the merge below sources them from the cookie.
 */
const fetcher = async (path: string): Promise<User> => {
  const { data } = await authClient.get<ProfileResponse>(path);
  return {
    id: data.id,
    email: data.email,
    name: data.username ?? "",
    avatar: data.avatarUrl ?? undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

/**
 * SWR configuration for auth requests
 */
const swrConfig: SWRConfiguration<User> = {
  // Cache & Deduplication
  dedupingInterval: 60000, // Don't refetch for 60 seconds

  // Revalidation strategies
  revalidateOnFocus: true, // Re-fetch on tab focus to pick up scope/tier changes
  revalidateOnReconnect: true, // Keep: useful for network recovery
  revalidateIfStale: true, // Keep: respect cache staleness
  revalidateOnMount: false, // Disable mount revalidation (middleware already validated)

  // Session-keepalive is handled by the proactive /v1/refresh scheduler in
  // useAuth (driven by accessExpiresAt in the user_info cookie). SWR no
  // longer polls /v1/profile on a fixed interval.

  // Retry on errors - less aggressive
  shouldRetryOnError: true,
  errorRetryCount: 2, // Reduced from 3
  errorRetryInterval: 10000, // Increased from 5s to 10s

  // Performance
  focusThrottleInterval: 30000, // Increased from 5s to 30s (if focus revalidation enabled)

  // Callbacks for debugging (optional)
  onSuccess: (data) => {
    if (process.env.NODE_ENV === "development") {
      console.log("[useAuth] Profile fetched:", data.email);
    }
  },
  onError: (error) => {
    if (process.env.NODE_ENV === "development") {
      console.error("[useAuth] Fetch failed:", error.message);
    }
  },
};

/**
 * Module-scoped session keepalive owner. useAuth is called from many places
 * (~30 in this repo); without this, every consumer arms its own setTimeout,
 * mounts its own focus/online listeners, and fires its own parallel
 * /v1/refresh. Subscribers receive a `notify` callback that they invoke their
 * own refreshCookie from — keeping per-instance cookie state in sync without
 * shared mutable state.
 */
type SessionOwnerSubscriber = (reason: string) => void;
const sessionOwner = (() => {
  const subscribers = new Set<SessionOwnerSubscriber>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let scheduledFor: number | null = null;
  let lastFiredAt: number | null = null;
  const MIN_REFIRE_MS = 30_000;
  let focusHandler: (() => void) | null = null;
  let onlineHandler: (() => void) | null = null;

  function notifyAll(reason: string) {
    for (const sub of subscribers) sub(reason);
  }

  function readAccessExpiresAtFromCookie(): number | null {
    const cookie = readUserInfoCookie();
    return cookie?.accessExpiresAt ?? null;
  }

  function arm() {
    if (typeof window === "undefined") return;
    const accessExpiresAt = readAccessExpiresAtFromCookie();
    if (!accessExpiresAt) {
      // No proactive scheduling possible until first refresh populates the
      // field. Reactive 401-refresh in the network client covers this gap.
      if (timer) {
        clearTimeout(timer);
        timer = null;
        scheduledFor = null;
      }
      return;
    }
    const REFRESH_LEAD_MS = 60_000;
    const fireAt = accessExpiresAt - REFRESH_LEAD_MS;
    if (timer && scheduledFor === fireAt) return;
    if (timer) clearTimeout(timer);
    scheduledFor = fireAt;
    // Never fire back-to-back. If a refresh did not move the expiry we read
    // (a cookie this tab cannot see rotated, clock skew, a BE bug), `fireAt`
    // stays in the past and a zero delay becomes a hot loop against the auth
    // API. The reactive 401 → refresh path still covers a genuinely expired
    // token in the meantime.
    const sinceLastFire = lastFiredAt === null ? Infinity : Date.now() - lastFiredAt;
    const delay = Math.max(0, fireAt - Date.now(), MIN_REFIRE_MS - sinceLastFire);
    timer = setTimeout(async () => {
      timer = null;
      scheduledFor = null;
      lastFiredAt = Date.now();
      const ok = await refreshAuth({ authApiUrl });
      notifyAll("post-proactive-refresh");
      // Pick up any tier/scope changes that came with the new access token.
      if (ok) globalMutate("/v1/profile");
      arm();
    }, delay);
  }

  function attachWindowListeners() {
    if (typeof window === "undefined") return;
    if (focusHandler) return;
    focusHandler = () => {
      notifyAll("window-focus");
      arm();
    };
    onlineHandler = () => {
      notifyAll("window-online");
      arm();
    };
    window.addEventListener("focus", focusHandler);
    window.addEventListener("online", onlineHandler);
  }

  function detachWindowListeners() {
    if (typeof window === "undefined") return;
    if (focusHandler) window.removeEventListener("focus", focusHandler);
    if (onlineHandler) window.removeEventListener("online", onlineHandler);
    focusHandler = null;
    onlineHandler = null;
  }

  return {
    subscribe(sub: SessionOwnerSubscriber) {
      subscribers.add(sub);
      if (subscribers.size === 1) {
        attachWindowListeners();
        arm();
      }
      return () => {
        subscribers.delete(sub);
        if (subscribers.size === 0) {
          if (timer) clearTimeout(timer);
          timer = null;
          scheduledFor = null;
          detachWindowListeners();
        }
      };
    },
    /** Called after any code path that may have rotated the cookie (e.g. SWR
     * settled after the network client's reactive /v1/refresh). Keeps the
     * timer aligned with the latest accessExpiresAt. */
    onCookieMaybeChanged() {
      arm();
    },
  };
})();

/**
 * Hook to fetch and manage authenticated user profile
 *
 * This hook:
 * - Fetches user profile from api.auth.l.gaz.codes
 * - Automatically includes HttpOnly cookie in request
 * - Auto-renews expired tokens via refresh_token cookie
 * - Caches result and deduplicates requests across components
 * - Retries on network errors
 *
 * @example
 * ```typescript
 * 'use client';
 * import { useAuth } from '@workspace/ui/hooks/useAuth';
 *
 * export default function ProfilePage() {
 *   const { user, loading, authenticated } = useAuth();
 *
 *   if (loading) return <Skeleton />;
 *   if (!authenticated) return <Redirect to="/login" />;
 *
 *   return <div>Welcome, {user.name}!</div>;
 * }
 * ```
 */
export function useAuth(options?: { requireAuth?: boolean }): UseAuthReturn {
  // Initial state is null on BOTH server and first client render. We hydrate
  // the cookie in an effect below so that the first render's output is
  // identical across SSR (no document.cookie) and CSR (cookie present). This
  // prevents hydration mismatches in consumers that branch on `authenticated`
  // — a mismatch anywhere in the tree causes React to discard the subtree and
  // remount, which used to flicker the avatar mid-session.
  //
  // The cost is one render with cookieUser=null (Sign-in button / loading
  // state) before the effect runs and swaps in the real avatar.
  //
  // The BE rotates user_info on every /v1/refresh and clears it on logout, so
  // we also re-read on window focus, online, post-proactive-refresh, and
  // every SWR transition. Cheap — it's a synchronous regex on document.cookie.
  const [cookieUser, setCookieUser] = useState<CookieUser | null>(null);
  // Tracks whether the post-mount cookie hydration has run. Consumers that
  // gate auth decisions (e.g. `requireAuth` redirect) must wait for this —
  // before hydration, cookieUser is null on purpose for SSR safety, NOT
  // because the user is logged out.
  const [hydrated, setHydrated] = useState(false);

  const refreshCookie = useCallback(() => {
    setCookieUser((prev) => {
      const next = readUserInfoCookie();

      // A transient null read must not sign the user out. The BE rotates
      // user_info on every /v1/refresh (clear-then-set), so reads triggered by
      // focus/online/SWR-settle can momentarily catch the cookie absent. Treat
      // an absent cookie as a logout ONLY when there's no still-valid session
      // to fall back to — i.e. the previous refresh token has also expired. If
      // the prior refreshExpiresAt is still in the future, keep `prev`: a real
      // logout/definitive-refresh-failure also expires the refresh token, so
      // "prev still valid + cookie missing" is a rotation race, not a sign-out.
      // This was what made the header avatar vanish after a background refresh.
      if (!next) {
        if (prev?.refreshExpiresAt && prev.refreshExpiresAt > Date.now()) {
          return prev;
        }
        return null;
      }

      // Keep the previous reference (avoids a re-render) only when every field
      // we surface is unchanged. `avatar`/`email` are included so a cookie
      // rotation that swaps the avatar actually propagates instead of being
      // deduped away.
      if (
        prev?.name === next.name &&
        prev?.email === next.email &&
        prev?.avatar === next.avatar &&
        prev?.refreshExpiresAt === next.refreshExpiresAt &&
        prev?.accessExpiresAt === next.accessExpiresAt
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  // Hydrate cookieUser from document.cookie after mount. This is the
  // SSR-safety effect — initial state is null so SSR/CSR match, then we read
  // the real cookie and trigger a re-render. Flip `hydrated` so consumers
  // that gate destructive actions on auth state (e.g. requireAuth redirect)
  // can tell "cookie not read yet" apart from "cookie read, user not logged in".
  useEffect(() => {
    refreshCookie();
    setHydrated(true);
  }, [refreshCookie]);

  // Subscribe to the module-scoped session owner. The owner runs ONE proactive
  // refresh timer and ONE pair of focus/online listeners per tab regardless of
  // how many components call useAuth(). When the owner detects a meaningful
  // event (focus, online, post-refresh), it notifies each subscriber so they
  // re-read the cookie into their own per-instance state.
  useEffect(() => {
    return sessionOwner.subscribe(() => refreshCookie());
  }, [refreshCookie]);

  const { data, error, isLoading, isValidating, mutate } = useSWR<User>(
    "/v1/profile",
    fetcher,
    swrConfig,
  );

  // Re-read the cookie after any SWR transition — a successful reactive
  // refresh on the network client's onUnauthorized path will have rotated the
  // cookie. Also nudge the session owner so it re-arms its timer against the
  // (possibly new) accessExpiresAt.
  useEffect(() => {
    refreshCookie();
    sessionOwner.onCookieMaybeChanged();
  }, [data, error, refreshCookie]);

  // Compose the user from SWR profile + the user_info cookie. Prefer the
  // freshly-fetched profile for most fields, but fall back to the cookie for
  // presentational fields the profile may omit or null out — chiefly `avatar`
  // (and its initials/bg). Using a bare `data ?? cookieUser` made the two
  // disagree: a tab on profile data showed initials while a cookie-only tab
  // showed the image, and a focus revalidation that returned no avatar made
  // the image vanish mid-session. Merging keeps the avatar stable everywhere.
  const user: User | undefined = useMemo(() => {
    if (!data && !cookieUser) return undefined;
    if (!data) return cookieUser as User;
    if (!cookieUser) return data;
    return {
      ...data,
      // `name` gets the same treatment as `avatar` below, and for the same
      // reason: an empty name silently changes what the avatar renders
      // (username initial → email-derived initials), so never let a blank
      // profile value displace a good cookie one.
      name: data.name || cookieUser.name || "",
      avatar: data.avatar ?? cookieUser.avatar,
      initials: data.initials ?? cookieUser.initials,
      avatarBgColor: data.avatarBgColor ?? cookieUser.avatarBgColor,
    };
  }, [data, cookieUser]);

  // Session validity is decided by the cookie, not by SWR's transient error
  // state. The BE clears `user_info` on logout and when a refresh-token
  // rotation fails definitively, so an absent/expired cookie is the
  // authoritative "you must sign in again" signal. A network blip on
  // /v1/profile is just a network blip — it shouldn't sign the user out.
  const cookieValid =
    !!cookieUser?.refreshExpiresAt && cookieUser.refreshExpiresAt > Date.now();
  const authenticated = !!user && cookieValid;

  // Redirect to login if requireAuth is set and user is not authenticated.
  // Gated on `hydrated` so we don't bounce the user before the cookie has had
  // a chance to be read post-mount (the SSR-stable initial render reports
  // authenticated=false even for logged-in users).
  useEffect(() => {
    if (options?.requireAuth && hydrated && !isLoading && !authenticated) {
      // Param name must be `return_to` (snake_case) — that's what the auth app's
      // useReturnTo() hook reads across every login flow. Sending `returnTo`
      // here silently dropped the redirect, stranding users on /profile after
      // sign-in instead of bouncing them back.
      const returnTo = encodeURIComponent(window.location.href);
      window.location.href = `${authUrl}/login?return_to=${returnTo}`;
    }
  }, [options?.requireAuth, hydrated, isLoading, authenticated]);

  const logout = async () => {
    try {
      await authClient.post("/v1/logout");
    } catch {
      // Proceed with client-side cleanup even if the API call fails
    }
    await mutate(undefined, { revalidate: false });
    window.location.href = `${authUrl}/login`;
  };

  return {
    user,
    // `loading` is true until the post-mount cookie hydration has run, even
    // if SWR has settled. Consumers that gate on `loading` (most do) will
    // naturally avoid acting on the SSR-stable initial render's
    // `authenticated=false` before we've had a chance to read the cookie.
    loading: (isLoading && !cookieUser) || !hydrated,
    validating: isValidating,
    error: error?.message || null,
    authenticated,
    refresh: mutate,
    logout,
  };
}

/**
 * Return type for useRequireScopes hook
 */
export interface UseRequireScopesReturn {
  /** True if the user has all requested scopes */
  granted: boolean;
  /** True while the scope check is in progress */
  loading: boolean;
  /** Scopes the user is missing (empty when granted) */
  missing: string[];
}

/**
 * Hook to verify the current user has specific scopes.
 *
 * Calls POST /v1/auth/verify-scopes on the auth API. The network client
 * handles 401/403 automatically — refreshing the JWT (which picks up any
 * newly-granted scopes) and retrying before surfacing an error.
 *
 * Only fires when useAuth reports the user is authenticated.
 *
 * @example
 * ```typescript
 * const { granted, loading, missing } = useRequireScopes("mcp_admin");
 * if (loading) return <Spinner />;
 * if (!granted) return <AccessDenied missing={missing} />;
 * ```
 */
export function useRequireScopes(...scopes: string[]): UseRequireScopesReturn {
  const { authenticated, loading: authLoading } = useAuth();

  // Stable key: only re-fetch when scopes list or auth state changes
  const key = authenticated ? `verify-scopes:${scopes.sort().join(",")}` : null;

  const { data, isLoading } = useSWR(
    key,
    async () => {
      const { data } = await authClient.post<{ granted: boolean; missing?: string[] }>(
        "/v1/auth/verify-scopes",
        { scopes },
      );
      return data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 60_000,
    },
  );

  return {
    granted: data?.granted ?? false,
    loading: authLoading || isLoading,
    missing: data?.missing ?? [],
  };
}

/**
 * Hook to fetch user preferences
 * Only fetches if user is authenticated
 *
 * @example
 * ```typescript
 * const { preferences, loading } = usePreferences();
 * ```
 */
export function usePreferences() {
  const { user } = useAuth();

  return useSWR(user ? "/v1/preferences" : null, fetcher, swrConfig);
}

/**
 * Hook to fetch connected OAuth accounts
 * Only fetches if user is authenticated
 *
 * @example
 * ```typescript
 * const { accounts, loading } = useConnectedAccounts();
 * ```
 */
export function useConnectedAccounts() {
  const { user } = useAuth();

  return useSWR(user ? "/v1/user/oauth-accounts" : null, fetcher, swrConfig);
}
